define('viewModels/widgets/TabbedContentView',[
    'lib/knockout',
    'lib/lodash4',
    'services/navHelper',
    'viewModels/ViewModel',
    'viewModels/NavMenu',
    'viewModels/SwitchedView',
    'viewModels/CoachingView'
], function (ko, _, navHelper, ViewModel, NavMenu, SwitchedView, CoachingView) {
    'use strict';

    /**
     * A ViewModel to help with the management of displaying a set of tabs and their
     * respective content.
     *
     * @param {array} tabMenuItems - an array of objects that must adhere to following
     * structure:
     *
     *     {
     *       hash: "#/route"     // A string representing the route that the browser
     *                           // should be set to when this item is selected
     *
     *       label: "messageKey" // A message key whose value is the text that will
     *                           // display for the nav button
     *
     *
     *
     *       cssClass: "string"    // (optional) A string with a space-separated list of css class names to apply.
     *     }
     *
     * @param {map} tabViewsMap  a map of view keys and some metadata to determine which
     *                           ViewModel and template should displayed for a tab.
     *
     *     {
     *       'viewKey': {
     *          view: ViewModelConstructor // A reference to the ViewModel's constructor that should be used for active tab's data
     *          template: "string"         // The path to the template that should be used as the content for the active tab
     *          persist: boolean           // (optional) Defaults to false. Indicates whether the tab view should be persistent.
     *          persistDOM: boolean // (optional) Indicates wehther the tab view should be constructed by the TabbedContentView (implies persist:true)
     *          arguments: Array           // (optional) Array of arguments to pass to the ViewModel constructor
     *       }
     *     }
     */
    var TabbedContentView = ViewModel.extend(function (tabMenuItems, tabViewsMap) {
        var self = this;

        //
        // Private Variables
        //
        var subViews = self.subView(SwitchedView);
        var subViewsMap = ko.observable({})
            .extend({ deferred: true });

        // ===============================================================================
        // Navigation
        // ===============================================================================

        self.navMenu = self.subView(NavMenu);

        // ===============================================================================
        // View Management
        // ===============================================================================
        
        // ===============================================================================
        // Coaching
        // ===============================================================================
        self.coachingView = self.subView(CoachingView);

        self.coachingFeatureToggles = self.computed({
            read: function() {
                return self.coachingView.coachingFeatureToggles();
            },
            write: function(value) {
                self.coachingView.coachingFeatureToggles(ko.unwrap(value));
            }
        });

        self.participants = self.computed({
            read: function() {
                return self.coachingView.participants();
            },
            write: function(value){
                self.coachingView.participants(ko.unwrap(value));
            }
        });

        self.conversationId = self.computed({
            read: function() {
                return self.coachingView.conversationId();
            },
            write: function(value){
                self.coachingView.conversationId(ko.unwrap(value));
            }
        });
        
        self.isActive = self.computed({
            read: function() {
                return self.coachingView.isActive();
            },
            write: function(value) {
                self.coachingView.isActive(ko.unwrap(value));
            }
        });


        /**
         * tab views constructed with an truthy `persistDOM` property
         * 
         * @type {array}
         */
        self.persistDOMTabViews = self.computed(function () {
            return _.filter(subViewsMap(), 'persistDOM');
        });

        self.currentViewData = self.computed(function () {
            var viewKey = subViews.getActiveView();
            var activeView = subViewsMap()[viewKey];
            var isPersistDOM = activeView && activeView.persistDOM;
            var view = isPersistDOM ? activeView : ko.unwrap(subViews[viewKey]);

            if (!view) {
                return null;
            }

            return {
                view: view,
                viewKey: viewKey,
                template: activeView.template,
                isPersistDOM: !!isPersistDOM
            };
        });

        self.containsTab = function (tabKey) {
            return !_.isUndefined(subViewsMap()[tabKey]);
        };

        // Returns true if the subView with given tabKey and navItem with given hash both exist.
        self.containsTabAndHash = function (tabKey, hash) {
            var viewToRemove = subViewsMap()[tabKey];
            if (viewToRemove) {
                return self.navMenu.containsNavItem({hash: hash});
            }
            return false;
        };

        self.addTabViews = function (tabMenuItems, tabViewsMap) {
            var newSubViewsMap = subViewsMap();

            _.each(tabMenuItems, function (tabMenuItem) {
                self.navMenu.addNavItem(tabMenuItem);
            });

            _.forIn(tabViewsMap, function(value, key) {
                newSubViewsMap[key] = _.assign({}, value);
                newSubViewsMap[key].viewKey = key;
                
                var args = Array.prototype.concat.call([
                    key,
                    // SwitchedView will skip construction if we pass no constructor
                    (value.persistDOM ? undefined : value.view)
                ], value.arguments);
                
                if (value.persistDOM) {
                    newSubViewsMap[key].view = self.subView.apply(self, [value.view].concat(value.arguments));
                } 

                if (value.persist || value.persistDOM) {
                    subViews.addPersistentView.apply(subViews, args);
                } else {
                    subViews.addView.apply(subViews, args);
                }
            });
            
            subViewsMap(newSubViewsMap);
        };

        self.removeTabViews = function() {
            subViewsMap({});
        };

        // Removes the navItem and subview associated with the tabKey and hash.
        // If both are not present, neither will be removed. If this causes the
        // active view to be removed, it is up to the caller to activate a different
        // view.
        self.removeTabView = function(tabKey, hash) {
            if (self.containsTabAndHash(tabKey, hash)) {
                self.navMenu.removeNavItem({hash: hash});
                subViews.invalidateViewInstance(tabKey);
                var newSubViewsMap = subViewsMap();
                delete newSubViewsMap[tabKey];
                subViewsMap(newSubViewsMap);
            }
        };

        self.removeActiveTabView = function () {
            self.navMenu.removeNavItem({ hash: window.location.hash });
            var activeView = subViews.getActiveView();
            subViews.disposeView(activeView);
            var newSubViewsMap = subViewsMap();
            delete newSubViewsMap[activeView];
            subViewsMap(newSubViewsMap);
            self.activateDefaultTabView();
        };

        self.activateDefaultTabView = function () {
            // Activate the first tab
            var navItems = self.navMenu.navItems();
            _.forIn(navItems, function (value) {
                if (window.location.hash !== value.hash) {
                    navHelper.navigateTo(value.hash);
                }
                return false;
            });

            _.forIn(subViewsMap(), function (value, key) {
                self.setActiveTabView(key);
                return false;
            });
        };

        self.setActiveTabView = function (/*tabKey, args*/) {
            return subViews.setActiveView.apply(subViews, arguments);
        };

        // ===============================================================================
        // Initialize
        // ===============================================================================

        self.addTabViews(tabMenuItems, tabViewsMap);
    });

    return TabbedContentView;
});

